import { useEffect, useRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router';
import { useShellSearchParams } from '../../lib/use-shell-search-params';
import { countryConfig } from '../../country';
import { HISPANIC_EDITION } from '../../lib/hispanic-edition';
import { track, SEARCH_QUERY_MAX_LEN } from '../../lib/funnel';
import { trackSearch } from '../../lib/tracking';
import { sendMetaEvent } from '../../lib/meta-client';
import { Seo } from '../../components/seo';
import { FilterBar } from '../../components/filter-bar';
import { CatalogFilters } from '../../components/catalog-filters';
import { TenderList } from '../../components/tender-list';
import { NoResultsAlert } from '../../components/no-results-alert';
import { NicheHint } from '../../components/niche-hint';
import { OrgHint } from '../../components/org-hint';
import { useListDensity } from '../../lib/list-density';
import { Pagination } from '../../components/pagination';
import { UpgradeBanner } from '../../components/upgrade-banner';
import { ChannelSubscribeCta } from '../../components/channel-subscribe-cta';
import { SaveSearchButton } from '../../components/save-search-button';
import { AlertWhenNote } from '../../components/alert-when-note';
import { AlertFilterLine } from '../../components/alert-filter-line';
import { AddCategoryNudge } from '../../components/add-category-nudge';
import { OnboardingBanner } from '../../components/onboarding-banner';
import { SourceFreshnessNotice } from '../../components/source-freshness-notice';
import { QueryError, SlowLoadNotice, TenderListSkeleton } from '../../components/query-error';
import { InfoHint } from '../../components/info-hint';
import { IfiProjectsPanel } from '../../components/ifi-projects-panel';
import { KindTabs } from '../../components/kind-tabs';
import { kindTabsOn } from '@tenders/shared';
import { IfiSourceTabs } from '../../components/ifi-source-tabs';
import { ScopeChips } from '../../components/scope-chips';
import { ExportButton } from '../../components/export-button';
import { RefreshIcon, DensityNormalIcon, DensityCompactIcon } from '../../components/icons';
import {
  fetchCatalog,
  fetchSemanticSearch,
  fetchCatalogStats,
  parseKindParam,
  parseIfiSourceParam,
  parseStatusParam,
  parseIntParam,
  parseIsoDateParam,
} from '../../lib/catalog-client';
import { listSavedSearches } from '../../lib/saved-search-client';
import { useAuth } from '../../lib/auth-context';
import { usePageTitle } from '../../lib/page-title';
import { useLocale, useT } from '../../i18n/locale-context';
import { localeCount, pluralRu, tendersWord } from '../../lib/format';
import { hrefSearchWholeCatalogue, hrefWithoutNarrowing } from '../../lib/catalog-narrowing';
import { SearchScopeLine } from '../../components/search-scope-line';
import { localePath } from '../../i18n/locale-path';
import { tenderPath } from '../../lib/slug';
import { listingPageHref, resolveListingPage } from '../../lib/pagination-paths';
import { COMPANY, HAS_TELEGRAM } from '../../lib/company';
import { LOCALES, type Locale } from '../../i18n/index';
import { uuid } from '../../lib/uuid';
import { ResultsCatalog } from './results-catalog';
import { hasEsLegalSet } from '../../content/legal/es/paths';
import { AiOnboardingEntry } from '../../components/ai-onboarding-entry';
import {
  ES_OPERATOR,
  ES_OPERATOR_IDENTIFIER,
  ES_OPERATOR_POSTAL_ADDRESS,
} from '../../content/legal/es/operator';

/** Mirrors SITE_URL in components/seo.tsx — absolute URLs are required in JSON-LD. */
const SITE_ORIGIN = countryConfig.brand.siteUrl;

/** MiLicita (`es`) is operated by AI CRM Inc, not by the Uzbek operator in
 *  `COMPANY` — the Organization node names the titular of the es legal pages. */
const ES_LEGAL = hasEsLegalSet(countryConfig.code);

/** Spain (`features.alertLanding`, review 21.10 «first hour»): the spotlight
 *  card sells the free alert with a «Sin tarjeta · N abiertas ahora» line, and
 *  its button works with nothing picked. Off elsewhere: the card as before. */
const ALERT_LANDING = countryConfig.features.alertLanding === true;

/** Spain (`features.searchScopeLine`, owner 23.10): a word typed with narrowing filters on
 *  keeps a «con los filtros activos» line and a one-tap way to the whole catalogue. */
const SEARCH_SCOPE_LINE = countryConfig.features.searchScopeLine === true;

/** Spain (review 21.10 «first hour», item 9): «Tu selección» says when the
 *  email will come and to which address. Only where alerts reach the address
 *  as it is (`messaging.emailWithoutConfirmation`) — the sentence promises a
 *  letter, with no «confirm your email first». */
const SAY_WHEN_EMAIL = countryConfig.messaging.emailWithoutConfirmation === true;

/** Spain (`features.aiOnboardingReentry`, QA 22.10 C11): «Configurar mis alertas
 *  con IA» on the first-ask card and under an empty result, for a signed-in
 *  reader with no saved search. Off elsewhere: never mounted. */
const AI_REENTRY = countryConfig.features.aiOnboardingReentry === true;

/** Spain (`features.pastLastPage`, QA 22.10 A2.7): a page number past the last
 *  page says so and links to page 1. Off elsewhere: the empty list as before.
 *  Read per render, not at import, so a test can switch it. */
function pastLastPageOn(): boolean {
  return countryConfig.features.pastLastPage === true;
}

/** The same listing at page 1 — the `/page/N` segment and `?page=` dropped,
 *  every filter kept. Works on the path as the browser has it (locale prefix
 *  included), so it goes to a plain router link. */
export function firstPageOf(pathname: string, params: URLSearchParams): string {
  const path = pathname.replace(/\/page\/\d+\/?$/, '') || '/';
  const next = new URLSearchParams(params);
  next.delete('page');
  const qs = next.toString();
  return qs ? `${path}?${qs}` : path;
}

const PAGE_SIZE = 20;

export interface CatalogListFilters {
  q?: string;
  region?: string;
  /** Buyer (заказчик) subscription axis — `?customer=<tin>`, reachable from a
   *  digest deep link (Task 5). Only the plain list honours it server-side
   *  (see CatalogFilters.customer in catalog-client.ts); kept in the query key
   *  regardless so a `?customer=` change never serves a stale cached page. */
  customer?: string;
  category?: string;
  ikpu?: string;
  /** Curated Niche (Spec #3) slug — same single axis as `category`/`ikpu`. */
  niche?: string;
  /** Тип закупки ('goods' | 'works' | 'services'). Ось, независимая от ниши. */
  kind?: string;
  status?: string;
  /** Каталожный слой ('ifi' | 'corporate' | 'gov'). Undefined lets the API
   *  apply its own default (gov + corporate) — MUST stay part of the cache
   *  key so a fixed-layer mount (e.g. /ifi) never serves the default listing
   *  from react-query's cache. */
  scope?: string;
  /** Институт внутри слоя ifi ('ungm' | 'adb' | …). Часть ключа кеша: без него
   *  выборка одного института отдавалась бы из кеша всего слоя. */
  ifiSource?: string;
  sort?: string;
  minBudget?: string;
  maxBudget?: string;
  deadlineFrom?: string;
  deadlineTo?: string;
  /** ISO-8601 lower bound on first-seen time — "new since" for digest deep links. */
  since?: string;
  /** Outcomes listing (`/results`). Part of the cache key: without it the awarded
   *  archive and the default browse listing would share an entry. */
  awarded?: boolean;
  /** /results' month window (`RESULTS_WINDOW_DAYS`). Part of the key so the
   *  windowed list and an unwindowed awarded list never share an entry. */
  outcomeDays?: number;
  /** Минус-слова через запятую. Обязательно часть ключа кеша: иначе список с
   *  исключением и без него делят одну запись, и вычеркнутый лот возвращается
   *  из кеша — то есть выглядит ровно как «фильтр не сработал». */
  exclude?: string;
  /** The /results card's state as one `criteria=` JSON (`lib/results-search.ts`).
   *  Part of the key, and `undefined` on a bare /results — so the prerender
   *  seed (awarded + page, no criteria) still hashes the same. */
  criteria?: string;
  /** A saved search's whole definition (`alert=`, Spain — see
   *  `savedSearchAlertParam`). Part of the key: the list it narrows and the
   *  bare catalogue must never share an entry. */
  alert?: string;
  page: number;
}

/** The default (unfiltered, first-page) catalog list — what `/` and `/catalog` render.
 *  Used to SSR-seed the home page (entry-server) so it isn't an empty loading shell. */
export const DEFAULT_CATALOG_FILTERS: CatalogListFilters = { page: 1 };

/**
 * Canonical React Query key for the catalog list, shared by the Catalog component
 * and the SSR seed (`entry-server.seedRouteQuery`). Both MUST build the key through
 * this helper so their hashes match — a mismatch is a cache miss, which re-empties
 * the prerendered home page. (React Query hashes object keys order-insensitively and
 * drops `undefined`, so the default filters hash to `{"page":1}`.)
 */
export function catalogListKey(filters: CatalogListFilters, locale: Locale) {
  return [
    'catalog',
    'list',
    {
      q: filters.q,
      region: filters.region,
      customer: filters.customer,
      category: filters.category,
      ikpu: filters.ikpu,
      niche: filters.niche,
      kind: filters.kind,
      status: filters.status,
      exclude: filters.exclude,
      scope: filters.scope,
      ifiSource: filters.ifiSource,
      sort: filters.sort,
      minBudget: filters.minBudget,
      maxBudget: filters.maxBudget,
      deadlineFrom: filters.deadlineFrom,
      deadlineTo: filters.deadlineTo,
      since: filters.since,
      awarded: filters.awarded,
      outcomeDays: filters.outcomeDays,
      criteria: filters.criteria,
      alert: filters.alert,
      page: filters.page,
    },
    locale,
  ];
}

/**
 * One consolidated summary line above the list: the (filtered) result count +
 * catalog-fixed signals (closing-soon, new-today) + the auto-update indicator.
 * Merges what used to be a separate stat-chips strip and a results-count row —
 * same info, one line, less vertical space. The bold count IS the former
 * "N активных" chip (identical when unfiltered), so nothing is duplicated.
 */
/** Status values whose results stay inside the default browse scope that
 *  `stats.active` counts (active + closing). Outside this set the "N из M"
 *  comparison is meaningless — see `countLabel` below. */
const STATUSES_WITHIN_BROWSE_SCOPE = new Set(['active', 'closing']);

function ResultsSummary({
  total,
  approximate,
  filtered,
  searching = false,
  liveTotal = false,
  fetching,
  scope,
  status,
  trailing,
}: {
  total: number | undefined;
  /** A typed query is active. Its results span EVERY layer (the list goes out
   *  with `scope=all`, supply requests and МФИ included), so they are not a
   *  subset of the browse count and get no «из M». */
  searching?: boolean;
  /**
   * The unfiltered count may be read from `/catalog/stats` instead of the
   * list's `total` — the plain browse catalogue only (never /results, whose
   * list is the awarded archive, not the open lots `stats.active` counts).
   *
   * Why (Igor's phone run, 17.10, guest on /uz): the page showed THREE totals —
   * «Hammasi 3517» (kind tabs), «3395 ta tender» (this line) and, once a word
   * was typed, «4444 tadan». The first two have one definition (gov+corporate,
   * active+closing, contact-gated); 3395 was the PRERENDERED list's total,
   * hours old, kept on purpose (`refetchOnMount: false`, the CLS note on the
   * list query), while the tabs and `stats` are fetched live on mount. The
   * stats request already runs for the «N новых» signal, so reading its
   * `active` here costs no extra request and makes the line agree with the
   * tabs. Until it arrives (and on the server) the list total stands, so the
   * prerendered HTML and the first client render still match.
   */
  liveTotal?: boolean;
  /** `total` is a LOWER BOUND, not a count — the hybrid search's semantic arm
   *  is bounded by construction and came back full. Renders «не менее N»; a
   *  precise number here would be the same lie the 300-cap told. */
  approximate?: boolean;
  filtered: boolean;
  fetching?: boolean;
  /** Rendered at the right end of the same line — the density toggle. On its
   *  own row it read as a second toolbar for a control that only restyles the
   *  list below it. */
  trailing?: React.ReactNode;
  /** Current status filter, or undefined for the default browse scope. */
  status?: string;
  /** Fixed catalog layer (e.g. 'ifi' on /ifi) — MUST be part of the query key
   *  too, or a gov-page visit's cached stats would leak into /ifi (and vice
   *  versa) via react-query's cache. Undefined is a no-op: the server applies
   *  its own default (gov + corporate), same as the plain catalog today. */
  scope?: string;
}) {
  const t = useT();
  const { locale } = useLocale();
  const { data: stats } = useQuery({
    queryKey: ['catalog', 'stats', scope],
    queryFn: () => fetchCatalogStats(scope),
  });
  const catalogTotal = stats?.active;

  // Count line: unfiltered → "2764 тендеров"; filtered → "42 из 2764 тендеров"
  // (keeps the catalog total visible even when narrowed down).
  //
  // The "N из M" form only holds while the filtered set is a SUBSET of what M
  // counts. `catalogTotal` is `stats.active`, i.e. active + closing — so asking
  // for closed/expired/archived (disjoint) or `all` (a superset) would render a
  // nonsense ratio like "727 из 81". In those cases show the bare count.
  const comparableToTotal = (!status || STATUSES_WITHIN_BROWSE_SCOPE.has(status)) && !searching;
  // Unfiltered browse: the live `stats.active` — see `liveTotal`. Same set as
  // the list (no filter, browse status, this layer), only fresher.
  const shown =
    liveTotal &&
    !filtered &&
    !searching &&
    !approximate &&
    comparableToTotal &&
    catalogTotal != null
      ? catalogTotal
      : total;
  const countLabel =
    shown == null
      ? t('catalog.loading')
      : approximate
        ? // Deliberately NOT the "N из M" form: a floor cannot be a numerator.
          t('result.countAtLeast', { count: localeCount(shown), word: tendersWord(shown, locale) })
        : filtered && catalogTotal != null && comparableToTotal
          ? // The noun agrees with the DENOMINATOR: «3 из 3771 тендеров».
            t('result.countOf', {
              count: localeCount(shown),
              total: localeCount(catalogTotal),
              word: tendersWord(catalogTotal, locale),
            })
          : t('result.count', { count: localeCount(shown), word: tendersWord(shown, locale) });

  // «Что означает это число?» — the count is scope-specific (open gov budget
  // procurements on gov/corporate; institution notices with no so'm budget on
  // /ifi), so the explanation follows the layer. Default (undefined/'gov'/'all')
  // uses the gov copy; 'corporate' and 'ifi' get their own.
  const hintKey =
    scope === 'ifi'
      ? 'result.hint.ifi'
      : scope === 'corporate'
        ? 'result.hint.corporate'
        : 'result.hint.gov';

  // Everything on ONE compact line: bold count, then muted signals each set off
  // by a middot (wraps gracefully on mobile; no separate second row).
  const metas: string[] = [];
  // `newToday` is a CATALOGUE-wide figure (`/catalog/stats`), so it only belongs
  // next to the catalogue-wide count: under a filter or a keyword it read
  // «0 тендеров · 306 новых» (2026-10-20 UX walk) — the same 306 whatever was
  // picked — and told the reader the filter was ignored.
  if (stats && stats.newToday > 0 && !filtered && !searching)
    // «1 новый», not «1 новых» — the noun agrees with the count (2026-10-06).
    // Spanish: «1 nueva», «5 nuevas» (licitaciones).
    metas.push(
      t('catalog.chips.newToday', {
        count: localeCount(stats.newToday),
        word:
          locale === 'es'
            ? stats.newToday === 1
              ? 'nueva'
              : 'nuevas'
            : pluralRu(stats.newToday, 'новый', 'новых', 'новых'),
      }),
    );

  return (
    // Count text and the action icons share ONE row. The count + its meta signals
    // live in a `flex-1` group that wraps INTERNALLY (so "· N новых" drops to a
    // second line on a narrow phone), while the icon cluster is `shrink-0` and
    // therefore always stays on the first line, inline with the count — instead of
    // the whole cluster wrapping to a row of its own. `items-start` keeps the icons
    // top-aligned when the count text wraps below them.
    <div className="mb-3 mt-4 flex items-start gap-2 text-[13px] text-ink-muted">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
        {/* `key` = the label: a new count is a NEW element, not a text update.
            Chrome's translator swaps the text node for a <font> of its own, and
            React's later update lands on the detached node — the phone kept
            «Cargando licitaciones…» over a loaded list (Spain, 22.10). A fresh
            element is translated afresh. No markup change. */}
        <span key={countLabel} className="inline-flex items-center gap-1 font-semibold text-ink">
          {countLabel}
          {shown != null && <InfoHint text={t(hintKey)} />}
        </span>
        {metas.map((m, i) => (
          <span key={i} className="flex items-center gap-2">
            <span aria-hidden className="text-edge">
              ·
            </span>
            <span>{m}</span>
          </span>
        ))}
      </div>
      {/* Auto-update hint + density/export toggle: a `shrink-0` cluster pinned to
          the right of the SAME row as the count. The refresh icon only appears
          WHILE the catalog refetches, fading in/out; its slot stays reserved so
          the row never shifts. */}
      <div className="flex shrink-0 items-center gap-2">
        <span
          role="img"
          title={t('result.updated')}
          aria-label={t('result.updated')}
          aria-hidden={!fetching}
          className={`shrink-0 text-brand transition-opacity duration-300 ${
            fetching ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <RefreshIcon className={`h-3.5 w-3.5 ${fetching ? 'animate-spin' : ''}`} />
        </span>
        {trailing}
      </div>
    </div>
  );
}

/**
 * Navigation state the onboarding sheet sets when it closes onto the catalogue
 * filtered by the search the reader just saved (2026-10-06, Igor). Read once
 * per navigation; a later filter change (a new URL, no state) drops it.
 */
export interface MySelectionState {
  mySelection: true;
}

/**
 * «Ваша подборка: N открытых лотов» — heads the list the reader landed on
 * after onboarding, so the first screen after «Сохранено» names what the
 * search found instead of an unfiltered catalogue. N is THIS list's own total
 * (default browse scope = open lots), the very number the rows below add up
 * to — not a second count that could disagree with them.
 */
export function MySelectionLine({ total, note }: { total: number; note?: ReactNode }) {
  const { t, locale } = useLocale();
  const lots =
    locale === 'uz'
      ? 'ochiq lot'
      : locale === 'kk'
        ? 'ашық лот'
        : locale === 'es'
          ? total === 1
            ? 'lote abierto'
            : 'lotes abiertos'
          : pluralRu(total, 'открытый лот', 'открытых лота', 'открытых лотов');
  return (
    <p
      role="status"
      className="mb-1 mt-3 rounded-lg bg-brand/10 px-3 py-2 text-[13.5px] font-semibold text-ink"
    >
      {t('catalog.mySelection', { count: total, lots })}
      {/* Spain: when the email will come (`AlertWhenNote`). Absent elsewhere. */}
      {note && <span className="mt-0.5 block text-[12.5px] font-medium text-ink-soft">{note}</span>}
    </p>
  );
}

/**
 * The catalogue route element. `/results` (`scope="awarded"`) is its own page
 * since 2026-10-07 — one search card instead of the topbar search, kind tabs
 * and filter bar (`ResultsCatalog`). A separate component TYPE, not a branch
 * inside one: react-router reuses the element between `/` and `/results`, and
 * two different hook lists in one component would break React's hook order.
 */
export function Catalog({ scope }: { scope?: string } = {}) {
  return scope === 'awarded' ? <ResultsCatalog /> : <CatalogListing scope={scope} />;
}

function CatalogListing({ scope }: { scope?: string } = {}) {
  // NOT react-router's `useSearchParams`. This page is PRERENDERED with no query
  // string and nginx serves that one file for `/?q=…` too, so reading `?q=` on the
  // hydration render made React throw #418 and rebuild the page from scratch. The
  // hook yields the prerender's (empty) params for that one render, then the real
  // URL — see lib/use-shell-search-params.ts, including why every `new
  // URLSearchParams(searchParams)` write below is safe (all of them are in event
  // handlers).
  const [searchParams, setSearchParams] = useShellSearchParams();
  const [density, setDensity] = useListDensity();
  const q = searchParams.get('q') ?? undefined;
  // Conversion funnel: a catalog browse, and (when present) the semantic search.
  useEffect(() => track('catalog_browsed', { list: scope ?? 'catalog' }), [scope]);
  useEffect(() => {
    if (!q) return;
    /*
     * Cap the query ONCE, here, so all four systems store the same thing.
     *
     * It used to be capped three different ways and left uncapped in a fourth:
     * the DIY beacon truncates to 80 server-side, the Meta CAPI bridge caps at
     * 200 and REJECTS anything longer (so a very long query fired the browser
     * Pixel with no server twin, silently), and PostHog received it unbounded —
     * because `track()` calls phCapture BEFORE the request reaches the server
     * that does the truncating. The 844-character pasted-text outlier the
     * server cap exists to stop was being retained in full by PostHog.
     *
     * Truncating at the source makes one limit authoritative and closes the
     * >200-char CAPI drop with it. The server cap stays as the backstop — this
     * beacon is public and a client-side limit is a suggestion.
     */
    const query = q.slice(0, SEARCH_QUERY_MAX_LEN);
    // `search_performed` is NOT emitted here — it waits for the result count.
    // See the effect below.
    //
    // Mirror it to Meta as the standard `Search` event — Pixel AND CAPI under
    // one dedup id, so an ad-blocked visitor still counts. This signal was
    // already being recorded first-party and simply never forwarded.
    //
    // The Meta leg stays on THIS effect, at intent time, on purpose: it is an
    // ad-attribution signal and delaying it behind a network round-trip would
    // change what the platform sees for no analytical gain.
    const metaEventId = uuid();
    trackSearch(query, metaEventId);
    sendMetaEvent('Search', metaEventId, undefined, query);
  }, [q]);
  const region = searchParams.get('region') ?? undefined;
  /** Buyer (заказчик) subscription axis — see `CatalogListFilters.customer`. */
  const customer = searchParams.get('customer') ?? undefined;
  const category = searchParams.get('category') ?? undefined;
  const ikpu = searchParams.get('ikpu') ?? undefined;
  const niche = searchParams.get('niche') ?? undefined;
  // `kind` comes off a user-editable URL param — narrow it with a guard, not
  // an assertion. An unvalidated cast here would let `?kind=garbage` reach
  // the API's `@IsIn` validation, 400 the request, and replace the whole
  // catalog list with the error card instead of degrading to "no filter".
  const kind = parseKindParam(searchParams.get('kind'));
  // Как и с `kind`: неизвестное значение считаем отсутствием фильтра, иначе
  // сервер вернёт 400 и вместо списка встанет карточка ошибки.
  const ifiSource = parseIfiSourceParam(searchParams.get('ifiSource'));
  // Same rule as `kind`/`ifiSource` above, applied to the rest: an unrecognised
  // value degrades to "no filter" instead of travelling on to a 400. `?status=open`
  // (not a real status — the API's are discovered/active/closing/…) or
  // `?minBudget=abc` (→ Number('abc') = NaN, serialized as the string "NaN") used
  // to 400 the list AND the category/kind/ifi-source facet counts in one render.
  const status = parseStatusParam(searchParams.get('status'));
  const sort = searchParams.get('sort') ?? undefined;
  const minBudget = parseIntParam(searchParams.get('minBudget'));
  const maxBudget = parseIntParam(searchParams.get('maxBudget'));
  const deadlineFrom = parseIsoDateParam(searchParams.get('deadlineFrom'));
  const deadlineTo = parseIsoDateParam(searchParams.get('deadlineTo'));
  const since = parseIsoDateParam(searchParams.get('since'));
  // Минус-слова: список через запятую. Никакой валидации — это свободный текст,
  // сервер сам обрежет по числу термов; неизвестного значения тут не бывает.
  //
  // Умышленно НЕ прогоняется через parse*Param выше: те охраняют поля с
  // замкнутым множеством значений (статус, целое число, дата ISO), где мусор
  // означает 400. `exclude` — свободный текст того же класса, что `q`, где
  // «неизвестного значения» действительно не существует. Безопасность здесь
  // держится на серверной стороне: DTO ограничивает длину, а SQL строится
  // на плейсхолдерах.
  const exclude = searchParams.get('exclude') ?? undefined;
  // A saved search's whole definition (`alert=`, Spain): the «+N más» link, the
  // sidebar row and «Ver la lista» open the list through it. Free text like
  // `exclude` — the server sanitizes it and treats garbage as no filter.
  const alert = searchParams.get('alert') ?? undefined;
  // Page number comes from the PATH first (`/page/3`, `/results/page/3`,
  // `/category/x/page/3`) and only then from `?page=`. The path form is the one
  // a crawler can reach — see the pagination routes in app.tsx — while the query
  // form stays for filtered views, whose state lives in the query string anyway.
  const page = resolveListingPage(useParams().page, searchParams.get('page'));
  const offset = (page - 1) * PAGE_SIZE;
  const { locale } = useLocale();
  const t = useT();
  const isIfi = scope === 'ifi';
  // Task 10 — cooperation.uz reverse-auction supply requests, `/supply`.
  // A real API `scope` (like 'ifi'), NOT a client-side reinterpretation like
  // `awarded` below: it flows straight through to `apiScope` and every child
  // that talks to the API. Thin, budgetless leads — see isTenderIndexable
  // (lib/tender-status.ts) for why the section AND its tender pages stay
  // noindex regardless of status.
  const isSupply = scope === 'supply';
  // `/results` — the awarded-lot archive. NOTE this is NOT an API `scope`: the
  // API's ScopeFilter is the catalog LAYER ('ifi' | 'corporate' | 'gov' | 'all')
  // and would 400 on 'awarded'. It translates to a status+awarded filter pair,
  // so `apiScope` must be sent instead of `scope` on every outbound call.
  const isAwarded = scope === 'awarded';
  // ⚠️ Pass `apiScope` — NOT `scope` — to every child that talks to the API.
  // The note above says "on every outbound call" and the list query honoured it,
  // but four child components were still handed the raw `scope`, so on
  // `/results` they each sent `scope=awarded` and the API rejected it:
  //   FilterBar       → /catalog/categories   400 → no category counts
  //   KindTabs        → /catalog/kind-counts  400 → no kind counts
  //   CatalogFilters  → /catalog/export       400 → Pro Excel export BROKEN
  //   ResultsSummary  → /catalog/stats        200, but scoped to the whole
  //                     catalogue rather than the archive
  // None of them use `scope` for link-building, so the substitution is total.
  // ResultsSummary also branches copy on `scope === 'ifi' | 'corporate'`;
  // 'awarded' matched neither, so `undefined` selects the same wording.
  // ⚠️ A KEYWORD SEARCH SPANS EVERY LAYER; browsing stays scoped.
  //
  // The API defaults to gov+corporate, and `ifi` deliberately has its own
  // section. That is right for browsing — the user chose a section — but wrong
  // the moment they type a word: they are looking for a tender, not for a
  // section, and nothing on screen tells them the search silently excluded one.
  //
  // Measured 2026-08-13 on a real report: a UNGM lot whose `title_ru` reads
  // «Предоставление услуг транскрибирования» returned ZERO for the query
  // «транскрибирования» on /catalog, and one row with `scope=all`. The data,
  // the index and the stemming were all fine; the layer filter dropped it
  // before ranking. All 96 IFI projects were unreachable from the main search
  // the same way.
  //
  // Sent from the CLIENT rather than changed in the server default on purpose:
  // `DEFAULT_SCOPES` also drives saved-search matching and supplier alerts, and
  // widening it there would push donor-financed lots into every subscriber's
  // digest. This opts in exactly one caller — the visible search box.
  //
  // Only when the user did not pick a section themselves: inside /ifi a query
  // must keep meaning "within МФИ".
  const apiScope = isAwarded ? undefined : q && !scope ? 'all' : scope;
  const effectiveStatus = isAwarded ? 'closed' : status;
  const awarded = isAwarded ? true : undefined;
  // Pro «Экспорт в Excel» payload. Lives here — beside the list query it mirrors —
  // rather than in CatalogFilters, since the icon-only button now rides the
  // results-count line next to the density toggle, not the filter bar. Reuses the
  // route-resolved values (apiScope/effectiveStatus/awarded), so the `/results`
  // and `/ifi` guards that once had to be re-derived come for free.
  //   ⚠️ A free-text query uses semantic ranking the export can't reproduce, so the
  //   button is hidden while searching (`showExport`). Deliberately omits
  //   niche/ikpu/ifiSource/exclude — same set the export endpoint honoured before.
  const showExport = !q?.trim();
  const exportFilters = {
    q: q || undefined,
    region: region || undefined,
    customer: customer || undefined,
    category: category || undefined,
    kind,
    status: effectiveStatus,
    awarded,
    scope: apiScope || undefined,
    // SORT_DEFAULT ('newest') in catalog-filters.tsx — the default sort is not a filter.
    sort: sort === 'newest' ? undefined : sort,
    minBudget: minBudget !== undefined ? Number(minBudget) : undefined,
    maxBudget: maxBudget !== undefined ? Number(maxBudget) : undefined,
    deadlineFrom,
    deadlineTo,
    lang: locale,
  };
  // The mobile app-bar title must match the visible H1 below — otherwise the
  // bar reads "Каталог тендеров" while the page is the IFI section. Reuses
  // the existing ifi.page.h1 copy (short label, same convention as
  // category.tsx/region.tsx's usePageTitle calls) rather than adding new keys.
  usePageTitle(
    isIfi
      ? t('ifi.page.h1')
      : isSupply
        ? t('supply.page.h1')
        : scope === 'awarded'
          ? t('results.page.h1')
          : t('catalog.title'),
  );
  const { status: authStatus, user: authUser } = useAuth();
  // The Save button is offered whenever a filter is actually picked — for authed
  // users it opens the save popover; for guests it stashes the search and routes
  // to /register (auto-saved post-signup). It stays disabled on an empty catalog.
  const hasFilter = Boolean(q || region || category || ikpu || niche);

  // Activation guidance: a signed-in user with no saved search yet. Highlight the
  // Category dropdown until they pick one, then the "Сохранить поиск" button.
  const { data: savedSearches } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: listSavedSearches,
    enabled: authStatus === 'authed',
  });
  // Only once the list has ARRIVED: while it loads, «no saved search» is not
  // known yet, and treating it as zero flashed the spotlight card at a reader
  // who has searches (owner, 2026-10-10).
  const needsActivation =
    authStatus === 'authed' && savedSearches !== undefined && savedSearches.length === 0;

  const location = useLocation();
  const mySelection = (location.state as Partial<MySelectionState> | null)?.mySelection === true;
  // The same stats request `ResultsSummary` makes (same key — one fetch), read
  // here so the pager's «Показано … из N» can print the number the line above
  // the list prints (`liveTotal`): on the plain unfiltered browse that is the
  // live `stats.active`, not the (prerendered, hours-old) list total.
  const { data: liveStats } = useQuery({
    queryKey: ['catalog', 'stats', apiScope === 'all' ? undefined : apiScope],
    queryFn: () => fetchCatalogStats(apiScope === 'all' ? undefined : apiScope),
    enabled: !isAwarded,
  });
  // Spain's spotlight line — «Sin tarjeta · 1.234 abiertas ahora», the live
  // count once the stats request (the one above) has answered.
  const spotlightNote = ALERT_LANDING
    ? [
        t('catalog.spotlight.noCard'),
        ...(liveStats?.active
          ? [t('catalog.spotlight.openNow', { count: localeCount(liveStats.active) })]
          : []),
      ].join(' · ')
    : undefined;
  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: catalogListKey(
      {
        q,
        region,
        customer,
        category,
        ikpu,
        niche,
        kind,
        status: effectiveStatus,
        scope: apiScope,
        ifiSource,
        sort,
        minBudget,
        maxBudget,
        deadlineFrom,
        deadlineTo,
        since,
        awarded,
        exclude,
        alert,
        page,
      },
      locale,
    ),
    queryFn: () =>
      // A free-text query goes to the hybrid semantic+keyword search (ranked by
      // relevance); the facet/range filters drive the plain catalog list.
      // `ikpu`/`niche` (the same single niche axis as `category`) DO carry over
      // to the search path — GET /catalog/search hierarchically filters by them
      // just like the plain list (see SearchController/SearchService) — so a
      // niche picked before typing a query keeps narrowing the results instead
      // of silently dropping while its chip stays shown as active. What the
      // search endpoint genuinely does NOT accept: `sort` and the budget/
      // deadline range filters (`minBudget`, `maxBudget`, `deadlineFrom`,
      // `deadlineTo`, `since`) — relevance ordering wins over `sort`, and there's
      // no range-filter support server-side. `ifiSource` DOES carry over now
      // (#53) — the search repository resolves it with a scalar subquery on
      // tender_sources in all three of its query builders.
      q && q.trim()
        ? fetchSemanticSearch({
            q,
            region,
            // GET /catalog/search now ANDs `customer` into the hybrid arms'
            // WHERE (`t.customer_tin =`), mirroring the plain list's
            // `where.customerTin` — a buyer pin + keyword narrows the same
            // way in both browse and the alert matcher (Task 7b).
            customer,
            category,
            ikpu,
            niche,
            status: effectiveStatus,
            scope: apiScope,
            kind,
            ifiSource,
            // `awarded` and the budget bounds now ride along. They used not to
            // be sent at all, which is why typing a query on /results returned
            // 1 lot out of 5 950: the one filter that defines the archive was
            // dropped at the client. The endpoint answers a request carrying
            // them through the catalog list engine (no semantic arm — the
            // awarded archive is ~0 % embedded anyway).
            awarded,
            minBudget: minBudget ? Number(minBudget) : undefined,
            maxBudget: maxBudget ? Number(maxBudget) : undefined,
            // Минус-слова доезжают и сюда: гибридные плечи их не выражают,
            // поэтому сервер уводит такой запрос на списочный движок. Не
            // отправить их здесь — значит показать при вводе запроса ровно тот
            // лот, который пользователь только что исключил.
            exclude,
            offset,
            limit: PAGE_SIZE,
            lang: locale,
          })
        : fetchCatalog({
            q,
            region,
            customer,
            category,
            ikpu,
            niche,
            kind,
            status: effectiveStatus,
            scope: apiScope,
            awarded,
            ifiSource,
            sort,
            minBudget: minBudget ? Number(minBudget) : undefined,
            maxBudget: maxBudget ? Number(maxBudget) : undefined,
            deadlineFrom,
            deadlineTo,
            since,
            exclude,
            alert,
            offset,
            limit: PAGE_SIZE,
            lang: locale,
          }),
    // The list is SSR-seeded from the prerendered snapshot, but that seed carries
    // a prerender-time `dataUpdatedAt` (hours old), so the default on-mount
    // refetch fires immediately and swaps the list for a different-height fresh
    // result — the dominant mobile CLS on the homepage (the catalog turns over
    // constantly, "N новых сегодня"). The prerendered snapshot IS the edge-cached,
    // indexed content, so keep it on load; the next filter/navigation is a new
    // query key that fetches fresh anyway, and background refresh on window focus
    // still applies. Trades a few minutes of first-paint liveness for a stable paint.
    refetchOnMount: false,
  });

  /*
   * `search_performed`, emitted ONCE per query and carrying the RESULT COUNT.
   *
   * The count is the whole point. Prod holds ~4 000 of these events and every
   * one of them records only the words typed, so the single question the data
   * exists to answer — which searches come back empty — could not be asked of
   * it. Answering it on 2026-08-13 meant replaying the logged queries against
   * the CURRENT catalogue and hoping it had not moved, which found that the
   * commonest zero-result searches on the site are the product's own niche
   * names. That reconstruction should not have been necessary twice.
   *
   * Deliberately fired on settle (data OR error) rather than on keystroke:
   * - a ref guards it so paging, refetches and background updates do not
   *   re-emit; one search is one event, as before;
   * - the error branch still emits, without `total`, so a failed request
   *   under-counts nothing. Emitting only on success would have quietly
   *   deleted every search made during an API blip from the funnel.
   */
  const reportedSearchRef = useRef<string | null>(null);
  useEffect(() => {
    if (!q) return;
    const query = q.slice(0, SEARCH_QUERY_MAX_LEN);
    if (reportedSearchRef.current === query) return;
    if (isLoading) return;
    if (!isError && data === undefined) return;
    reportedSearchRef.current = query;
    track('search_performed', {
      q: query,
      // (No `semantic` flag: this event fires ONLY for a free-text query, which
      // always goes through the hybrid semantic engine — so the flag was always
      // `true` and carried no signal. Dropped rather than left as a dead key.)
      ...(isError ? {} : { total: data?.total ?? 0 }),
    });
  }, [q, data, isLoading, isError]);

  // No per-layer lock any more. `/ifi` cards used to render as inert teasers
  // for a non-Pro viewer (derived here from `data.viewerIsPro` plus the mount's
  // `scope` prop, against `ifi-locked-*` placeholder ids the API shipped). The
  // donor layer now follows the same rule as the government catalog — free to
  // read, Pro to act — so the list renders identically on every scope and the
  // paywall lives on the detail page's contacts/documents alone.

  /**
   * Bare canonical path of this listing, or null when there is no crawlable one.
   * `/ifi` is excluded on purpose — it is a Pro teaser whose rows render locked,
   * so paginating it would mint indexable URLs for content a crawler cannot read.
   * `/supply` is excluded for a different reason: the whole section is `noindex`
   * (thin, budgetless leads), so minting paginated hub URLs for it would be pure
   * crawl waste with nothing to show for it.
   */
  const listingBasePath = isAwarded ? '/results' : isIfi || isSupply ? null : '/';

  /** Any query-string state at all — a filtered/searched/sorted view. */
  const hasQueryState = Boolean(
    q ||
    region ||
    customer ||
    category ||
    ikpu ||
    niche ||
    kind ||
    status ||
    ifiSource ||
    sort ||
    minBudget ||
    maxBudget ||
    deadlineFrom ||
    deadlineTo ||
    since ||
    // Иначе отфильтрованный вид продолжил бы претендовать на индексируемый
    // канонический адрес и плодить краулерные URL на каждую комбинацию слов.
    exclude ||
    alert,
  );

  /**
   * Path builder handed to <Pagination> so the pager renders real links.
   * Deliberately undefined on a filtered view: these paths carry no query
   * string, so linking would silently drop the user's filters AND mint a
   * crawlable URL for every filter permutation (classic index bloat). Filtered
   * paging stays on `?page=` via `goToPage`, which is fine — those views are
   * `noindex`-irrelevant because the canonical below points at the bare path.
   */
  const pageHref =
    listingBasePath && !hasQueryState
      ? (n: number): string => listingPageHref(listingBasePath, n)
      : undefined;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  // Spain: lots exist, but not on this page — `?page=999` (QA 22.10, A2.7) drew
  // «No hay licitaciones que cumplan los filtros actuales» under «504
  // licitaciones» and a pager reading «501–504 de 504 · 26 / 26».
  const pastLastPage =
    pastLastPageOn() && !!data && data.total > 0 && data.items.length === 0 && page > 1;
  // SELF-canonical, not a canonical back to page 1. Pointing every paginated
  // page at page 1 would tell Google to drop them — and they are the only thing
  // giving the deep catalog an internal link, which is the whole reason they
  // exist. Google deprecated rel=next/prev as an indexing signal but still
  // follows them as links, so they cost nothing and help discovery.
  const canonicalPath = pageHref ? pageHref(page) : undefined;
  const prevPath = pageHref && page > 1 ? pageHref(page - 1) : undefined;
  const nextPath = pageHref && page < totalPages ? pageHref(page + 1) : undefined;

  function goToPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next);
  }

  /**
   * Display name for the `?customer=<tin>` chip. The URL only ever carries the
   * TIN (a digest deep link has no companion name param), so the name is read
   * off the current page's own rows — every one of which shares the same
   * `customerTin` while the filter is doing the narrowing (Task 7b: search
   * now honours `customer` too, same as the plain list).
   *
   * `!q` still guards the READ, though — not because search rows are
   * unfiltered any more, but because the hybrid search arm resolves each row
   * via `CatalogService.detail()` per id, which always passes `buyerName:
   * null` (see `toTenderView`'s call site in `detail()`). Only the plain
   * list's `list()` batch-resolves org names via `resolveBuyerNames`. So a
   * search row's `buyerName` is not a reliable signal yet; falling back to
   * the bare TIN (CatalogFilters' own `customerName ?? customer`) stays
   * honest about what the page can resolve today rather than showing a blank
   * or stale label.
   */
  const customerName = customer && !q ? (data?.items?.[0]?.buyerName ?? undefined) : undefined;

  // One number on the page (owner's phone run, 17.10): the pager caption
  // repeats the count `ResultsSummary` shows above the list — the live stats
  // count on the plain unfiltered browse (no filter, no query, exact total,
  // not /results), the list total otherwise. Page arithmetic keeps `data.total`.
  const anyListFilter = Boolean(
    q ||
    region ||
    customer ||
    category ||
    ikpu ||
    niche ||
    ifiSource ||
    kind ||
    status ||
    minBudget ||
    maxBudget ||
    deadlineFrom ||
    deadlineTo ||
    alert,
  );
  // `totalIsExact` is absent on the prerendered snapshot (`window.__RQ_STATE__`
  // carries `{items,total,offset,limit}` only) — absent means exact, the same
  // reading `catalog-client` gives an API envelope without it. Reading absence
  // as "approximate" kept the stale prerendered total on screen (3637 beside a
  // live 3676 on 19.10).
  const pagerShownTotal =
    !isAwarded && !anyListFilter && data?.totalIsExact !== false && liveStats?.active != null
      ? liveStats.active
      : undefined;

  return (
    <>
      {isIfi ? (
        // /ifi is a distinct, publicly indexable page (teaser lock, not a hard
        // wall — see IfiPaywallBanner) with its OWN canonical, so unlike /catalog
        // it must NOT collapse into the home page's SEO identity.
        <Seo
          title={t('ifi.seo.title')}
          description={t('ifi.seo.description')}
          canonical="/ifi"
          lang={locale}
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: `${countryConfig.brand.name} IFI Projects`,
            // Locale-correct self URL: this hardcoded the ru path, so the uz page
            // pointed its own ItemList at the ru URL.
            url: `${SITE_ORIGIN}${localePath('/ifi', locale)}`,
            // Was an empty shell — `name` + `url` and nothing else — on a page
            // listing ~97 tenders. Same fix already applied to the home catalog
            // below; the /ifi branch was missed.
            numberOfItems: data?.items?.length ?? 0,
            itemListElement: (data?.items ?? []).map((item, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `${SITE_ORIGIN}${localePath(tenderPath(item.slug, item.id), locale)}`,
              name: item.title,
            })),
          }}
        />
      ) : isSupply ? (
        // /supply is deliberately `noindex`: cooperation.uz reverse-auction supply
        // requests are thin, budgetless leads (no so'm budget field at all) — the
        // same crawl-budget caution that keeps them out of the default browse
        // scope. Unlike /ifi it still gets its own title/description/canonical
        // (real page, just not one we want crawled), so a direct visit or an
        // internal nav click never falls back to the home catalog's SEO identity.
        <Seo
          title={t('supply.seo.title')}
          description={t('supply.seo.description')}
          canonical="/supply"
          lang={locale}
          noIndex
        />
      ) : (
        <Seo
          title={
            isAwarded
              ? t(page > 1 ? 'seo.results.titlePaged' : 'seo.results.title', { page })
              : page > 1
                ? t('seo.catalog.titlePaged', { page })
                : t('seo.catalog.title')
          }
          description={isAwarded ? t('seo.results.description') : t('seo.catalog.description')}
          // Home is the catalog now; canonicalize both / and /catalog to the root.
          // On a paginated page this is the page's OWN path (see canonicalPath):
          // canonicalizing /page/7 back to / would ask Google to drop the very
          // pages that give the deep catalog its only internal links.
          canonical={canonicalPath ?? '/'}
          prev={prevPath}
          next={nextPath}
          lang={locale}
          // «página 999» was `index, follow` (QA 22.10, A2.7). Spain only.
          {...(pastLastPage ? { noIndex: true } : {})}
          jsonLd={{
            '@context': 'https://schema.org',
            // The home page is the entity home, so it carries the sitewide
            // Organization + WebSite nodes. They existed nowhere before, which
            // left "UzTender.com" as an unresolvable entity — a brand search still
            // surfaces the unrelated uztender.uz. @id-linked so other pages'
            // schema can reference them instead of restating them.
            '@graph': [
              {
                '@type': 'Organization',
                '@id': `${SITE_ORIGIN}/#organization`,
                name: countryConfig.brand.name,
                legalName: ES_LEGAL ? ES_OPERATOR.name : COMPANY.legalName,
                url: `${SITE_ORIGIN}/`,
                logo: `${SITE_ORIGIN}/icon-512.png`,
                description: t('seo.catalog.description'),
                // Verified external presence so search/AI engines can resolve
                // "UzTender.com" to a real, independently-checkable entity (the
                // official Telegram channel used across the site). Don't add
                // profiles that don't exist.
                ...(HAS_TELEGRAM ? { sameAs: [COMPANY.telegramUrl] } : {}),
                // Registry identifiers and a real postal address. These are the
                // strongest entity signals available to a young brand with almost no
                // external links (GSC Links recorded THREE referring domains on
                // 2026-08-03), because unlike a backlink they are independently
                // checkable against the state register: `taxID`/`vatID` resolve
                // ООО "FASTAGO" to one legal person, and that is what lets a search
                // or AI engine tell "UzTender.com" apart from the unrelated
                // uztender.uz it currently gets confused with.
                //
                // Every value comes from COMPANY — the same constant behind the
                // оферта, /requisites and the printable invoice — so the schema and
                // the legally-published requisites cannot drift apart. Do not
                // hand-copy them here.
                //
                // MiLicita (`es`): the titular of the Spanish legal set, AI CRM Inc,
                // its US address (`ES_OPERATOR`) and its Delaware file number as an
                // `identifier`. No taxID/vatID — none is published (owner, 2026-10-21:
                // the aviso legal identifies the company by that registry number).
                ...(ES_LEGAL
                  ? { identifier: ES_OPERATOR_IDENTIFIER }
                  : { taxID: COMPANY.inn, vatID: COMPANY.vatReg }),
                address: ES_LEGAL
                  ? ES_OPERATOR_POSTAL_ADDRESS
                  : {
                      '@type': 'PostalAddress',
                      streetAddress: COMPANY.address,
                      addressLocality: 'Tashkent',
                      addressCountry: 'UZ',
                    },
                contactPoint: {
                  '@type': 'ContactPoint',
                  contactType: 'customer support',
                  email: COMPANY.email,
                  // No `telephone` here on purpose: the phone was pulled from every
                  // public contact surface and now lives ONLY in the requisites card.
                  availableLanguage: [...LOCALES],
                },
              },
              {
                '@type': 'WebSite',
                '@id': `${SITE_ORIGIN}/#website`,
                name: countryConfig.brand.name,
                url: `${SITE_ORIGIN}/`,
                publisher: { '@id': `${SITE_ORIGIN}/#organization` },
                inLanguage: locale,
                // Declares the on-site catalog search so Google can render a
                // sitelinks searchbox. The `?q=` param is the real catalog
                // filter (see catalog-client.ts). Per-locale target keeps the
                // ru/uz surfaces distinct.
                potentialAction: {
                  '@type': 'SearchAction',
                  target: {
                    '@type': 'EntryPoint',
                    urlTemplate: `${SITE_ORIGIN}${localePath('/', locale)}?q={search_term_string}`,
                  },
                  'query-input': 'required name=search_term_string',
                },
              },
              {
                '@type': 'ItemList',
                // Spain names it in the page's language (audit 2026-10-21: the
                // English «MiLicita Catalog» on a Spanish-only site). UZ/KZ keep
                // the name they have always shipped.
                name: HISPANIC_EDITION
                  ? `Catálogo de licitaciones de ${countryConfig.brand.name}`
                  : `${countryConfig.brand.name} Catalog`,
                // Locale-correct self URL: this hardcoded the ru root, so
                // https://uztender.com/uz declared its ItemList url as the ru
                // homepage.
                url: `${SITE_ORIGIN}${localePath('/', locale)}`,
                numberOfItems: data?.items?.length ?? 0,
                // Was an empty shell: an ItemList with no itemListElement states
                // nothing at all. Now lists the tenders actually rendered on this
                // page, in display order, pointing at their canonical URLs.
                itemListElement: (data?.items ?? []).map((item, i) => ({
                  '@type': 'ListItem',
                  position: i + 1,
                  url: `${SITE_ORIGIN}${localePath(tenderPath(item.slug, item.id), locale)}`,
                  name: item.title,
                })),
              },
            ],
          }}
        />
      )}

      {/* One shape for every catalog variant: an sr-only H1, then the
          onboarding strip, then (where the page needs one) a description.
          /ifi and /results used to render a VISIBLE h1 + intro ABOVE the strip,
          which repeated the title already shown in the topbar and pushed
          everything else down.

          The H1 stays in the DOM for SEO — sr-only is hidden from sight, not
          from crawlers — and the descriptions stay VISIBLE, which is what
          actually earns the indexing: /ifi has to explain why its cards carry no
          som budget, and /results is a page crawlers arrive at cold with no
          query context. Only the duplicated heading goes. */}
      <h1 className="sr-only">
        {isIfi
          ? t('ifi.page.h1')
          : isSupply
            ? t('supply.page.h1')
            : isAwarded
              ? t('results.page.h1')
              : /* Head-term-rich SEO title, not the generic «Каталог тендеров»
                   (which remains the mobile app-bar label via usePageTitle). */
                t('seo.catalog.title')}
      </h1>

      <OnboardingBanner />

      {/* MiLicita: «PLACSP no publica licitaciones nuevas desde …» while its main feed is
          silent — client-only (fetched after mount), nothing on UZ/KZ or when fresh. */}
      {!isIfi && !isSupply && !isAwarded && <SourceFreshnessNotice />}

      {(isIfi || isSupply || isAwarded) && (
        <p className="mb-4 text-[13px] leading-relaxed text-ink-soft">
          {isIfi
            ? t('ifi.page.intro')
            : isSupply
              ? t('supply.page.intro')
              : t('results.page.intro')}
        </p>
      )}

      <FilterBar
        // Highlight the category picker (orange ring + «Выбрать категорию» label)
        // for ANYONE browsing unfiltered, not just activation-eligible signed-in
        // users — many first-time visitors don't realise they can filter and just
        // scroll. It reverts to «Все» the moment a category/niche is picked. The
        // heavier `emphasize` card + nudge banner stay scoped to `needsActivation`.
        highlightCategory={!category && !niche}
        emphasize={needsActivation}
        // The unfiltered catalogue's first ask, as a card (Igor, 06.10): for a
        // guest, or an account that has no saved search yet. Not on the awarded
        // archive, where «покажем подходящие тендеры» would promise open lots.
        // `authStatus !== 'authed'` is true for 'loading' as well, so the
        // prerender and the first client render agree (no #418). While it is
        // 'loading' the card is only MARKED (`spotlightPending`), and CSS keeps
        // it invisible in a browser whose readable `uzt_authed` cookie says it
        // has a session (index.html + globals.css) — from the first paint, not
        // after hydration. Once signed in it waits for the saved-search list
        // (`needsActivation` is false until it arrives). So a guest sees it as
        // before, a reader with searches never, and a reader without searches
        // once, when the list arrives (owner, 2026-10-10).
        spotlight={
          !category &&
          !niche &&
          !ikpu &&
          !q &&
          !isAwarded &&
          (authStatus !== 'authed' || needsActivation)
        }
        spotlightPending={authStatus === 'loading'}
        spotlightNote={spotlightNote}
        // Spain: the AI step again, for a signed-in reader who closed it (QA
        // 22.10, C11). It renders nothing for a guest or a reader with searches.
        spotlightAction={
          AI_REENTRY ? <AiOnboardingEntry className="w-full md:w-auto md:self-start" /> : undefined
        }
        scope={apiScope}
        status={effectiveStatus}
        awarded={awarded}
        nudge={<AddCategoryNudge variant="catalog" />}
        trailing={
          <SaveSearchButton
            q={q}
            region={region}
            category={category}
            ikpu={ikpu}
            nicheSlug={niche}
            // Also apiScope: `SavedSearch.scope` validates to gov|ifi|corporate,
            // so saving from /results 400'd outright. Falling back to the
            // column default (gov) is the right meaning anyway — someone
            // browsing past awards wants alerts on FUTURE tenders like them,
            // and "awarded" is not a thing you can subscribe to.
            scope={apiScope}
            highlight={needsActivation}
            // Spain: with nothing picked the button opens «¿Qué ofreces?»
            // (after /register for a guest) instead of sitting greyed out.
            disabled={!hasFilter && !ALERT_LANDING}
            emptyOpensOnboarding={ALERT_LANDING}
            // Spain: a guest's stash carries the budget band he filtered by.
            minBudget={minBudget ? Number(minBudget) : undefined}
            maxBudget={maxBudget ? Number(maxBudget) : undefined}
            // The list already knows its own total — the dead-filter warning
            // costs nothing extra.
            matchCount={data?.total}
          />
        }
      />
      {/* Region · Status · Sort · Budget · Deadline · Excel export — one unified
          component (inline bar on desktop, a Фильтры bottom sheet on mobile). */}
      <CatalogFilters
        scope={apiScope}
        // `isAwarded`, NOT `effectiveStatus != null`: only `/results` imposes a
        // status the URL cannot change. Everywhere else `effectiveStatus` is
        // just whatever the user picked, and handing that over hid the Status
        // control the instant it was used (prod repro 2026-09-21).
        statusFixedByRoute={isAwarded}
        awarded={awarded}
        customerName={customerName}
      />

      {/* Scope filter chips — «Все · МФИ · Запросы предложений» with the query's
          per-layer counts, each re-scoping while keeping the query. Renders
          itself only while a search is active (a search spans every layer;
          browsing does not). Not on the awarded archive, whose result set the
          search facets don't model. */}
      {!isAwarded && (
        <ScopeChips
          activeScope={isIfi ? 'ifi' : isSupply ? 'supply' : 'all'}
          didYouMean={q ? data?.didYouMean : undefined}
        />
      )}

      {/* Тип закупки: Все · Товары · Работы · Услуги. На /ifi и /supply скрыт —
          там подавляющее большинство кодов от слабого/иного классификатора
          (ADR 0003; cooperation.uz — не ИКПУ вовсе). Также скрыт на деплое без
          реального ИКПУ-классификатора (`features.priceIntel` = false, напр. KZ):
          `procurement_kind` там пуст, поэтому все вкладки дали бы ноль.
          Spain opts in through `features.kindTabs` (CPV fills the column). */}
      {kindTabsOn(countryConfig) && !isIfi && !isSupply && (
        <KindTabs scope={apiScope} status={effectiveStatus} awarded={awarded} />
      )}

      {/* Институты: Все · Всемирный банк · АБР · UNGM · ПРООН · AIIB (#53).
          Остаётся видимой и во время текстового поиска — с тех пор как
          GET /catalog/search научился фильтру по источнику, активный чип там
          действительно фильтрует. Счётчики при активном запросе не рисуются:
          выдача ранжируется гибридным поиском с порогом релевантности, и
          точного числа для чипа просто не существует (см. сам компонент). */}
      {isIfi && <IfiSourceTabs />}

      {/* The list is a saved search's own (`alert=`): say so, and offer the way
          back to the whole catalogue — none of the filter controls shows it. */}
      {alert && !q && <AlertFilterLine params={searchParams} />}

      {/* A word typed inside narrowing filters is searched inside them — say so, and offer
          the whole catalogue (at 0 results the empty card below already does). */}
      {SEARCH_SCOPE_LINE &&
        q?.trim() &&
        data &&
        data.total > 0 &&
        !isError &&
        hrefSearchWholeCatalogue(location.pathname, searchParams) && (
          <SearchScopeLine
            q={q.trim()}
            href={hrefSearchWholeCatalogue(location.pathname, searchParams) ?? '/catalog'}
          />
        )}

      {mySelection && data && data.totalIsExact !== false && !isError && (
        <MySelectionLine
          total={data.total}
          note={
            SAY_WHEN_EMAIL && authUser?.email ? (
              <AlertWhenNote
                email={authUser.email}
                filters={{ q, region, category, ikpu, nicheSlug: niche, scope: apiScope, alert }}
              />
            ) : undefined
          }
        />
      )}

      <ResultsSummary
        /* Density is a display preference, not a filter: it never touches the
           query, the URL or what is returned — so it rides on the results line
           rather than in the filter bar. */
        /* Icon toggle rather than the «Обычный/Компактный» text pills — the
           words cost ~150px and pushed the toggle onto its own wrapped line
           below the count on mobile; two 28px icon buttons fit inline on the
           count row. The label rides `title`/`aria-label` for a tooltip + a11y. */
        trailing={
          <div className="flex shrink-0 items-center gap-2">
            {/* Pro Excel export — icon-only, sits to the LEFT of the density
                toggle on the count row (was a full-width cell in the filter bar).
                ExportButton renders null for signed-out users; `showExport`
                additionally hides it during a semantic search. Sized to match the
                28px density buttons via a twMerge override of its h-11/w-11 default. */}
            {showExport && (
              <ExportButton filters={exportFilters} compact className="h-7 w-7 rounded-md" />
            )}
            <div className="inline-flex overflow-hidden rounded-md border border-edge">
              {(['normal', 'compact'] as const).map((mode) => {
                const label = t(mode === 'normal' ? 'list.density.normal' : 'list.density.compact');
                const Icon = mode === 'normal' ? DensityNormalIcon : DensityCompactIcon;
                return (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={density === mode}
                    aria-label={label}
                    title={label}
                    onClick={() => setDensity(mode)}
                    className={`flex h-7 w-7 items-center justify-center transition-colors ${
                      density === mode
                        ? 'bg-brand-light text-brand'
                        : 'bg-bone-surface text-ink-muted hover:text-ink'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })}
            </div>
          </div>
        }
        total={data?.total}
        // Absent ⇒ exact (the prerendered snapshot has no `totalIsExact`); only
        // an explicit `false` — the hybrid search's floor — is approximate.
        approximate={data ? data.totalIsExact === false : false}
        filtered={Boolean(
          q ||
          region ||
          customer ||
          category ||
          ikpu ||
          niche ||
          ifiSource ||
          kind ||
          status ||
          minBudget ||
          maxBudget ||
          deadlineFrom ||
          deadlineTo,
        )}
        searching={Boolean(q?.trim())}
        liveTotal={!isAwarded}
        fetching={isFetching}
        // A search's `apiScope` is 'all' (every layer, supply included), and
        // `stats?scope=all` was the third total on the page — «4444 tadan»
        // under a search, beside «Hammasi 3517». The line under a search shows
        // a bare count now, so its stats (the «N ta yangi» signal) stay on the
        // browse layer — the same cached request the unfiltered page made.
        scope={apiScope === 'all' ? undefined : apiScope}
        status={status}
      />

      {/* Reserve vertical space so the "Загрузка…" → results swap doesn't shift
          layout (CLS). min-h keeps the loading card and a populated list close
          enough in height that the transition is stable. */}
      <div className="min-h-[480px]">
        {isLoading ? (
          <>
            <SlowLoadNotice />
            <TenderListSkeleton />
          </>
        ) : isError ? (
          <QueryError onRetry={() => void refetch()} error={error} />
        ) : (
          <>
            {/* A keyword that matched NOTHING is the product's highest-intent
                moment, not a dead end: the user has just typed exactly what
                they want and the catalogue does not have it today. Offer the
                alert instead of a shrug. Only for a keyword search — an empty
                FILTER combination is a different (and usually fixable) problem,
                and the plain list message is the right answer there. */}
            {/* The niche banner sits ABOVE both branches: when a niche matched,
                the list below is (partly) its lots and the user has to be told;
                when several matched, nothing was substituted and these are the
                choices. Either way it precedes the "we have nothing" card,
                because that card's premise is false whenever a niche matched —
                it offers an alert for a tender the catalogue already holds. */}
            {/* An exact lot-number hit (`lotNumberMatch`, set by the API only
                on that branch): one quiet line so the reader knows the list IS
                the lot they typed, not a fuzzy match on a number. */}
            {q && data?.lotNumberMatch && (data?.items.length ?? 0) > 0 && (
              <p data-testid="lot-number-match" className="mb-3 text-[13px] text-ink-muted">
                {t(data.total > 1 ? 'catalog.lotNumberMatch.many' : 'catalog.lotNumberMatch.one', {
                  n: data.lotNumberMatch,
                })}
              </p>
            )}
            {/* Spain: the query was CPV codes, so the list is their subtrees
                (`cpvCodeMatch`, set by the API only then) — said once, quietly,
                so «90910000» reads as the code's lots and not a text match. */}
            {q && (data?.cpvCodeMatch?.length ?? 0) > 0 && (
              <p data-testid="cpv-code-match" className="mb-3 text-[13px] text-ink-muted">
                {t('catalog.cpvCodeMatch', { codes: (data?.cpvCodeMatch ?? []).join(', ') })}
              </p>
            )}
            {q && (data?.orgHints?.length ?? 0) > 0 && (
              <OrgHint q={q} hints={data?.orgHints ?? []} />
            )}
            {q && (data?.nicheHints?.length ?? 0) > 0 && (
              <NicheHint q={q} niches={data?.nicheHints ?? []} applied={data?.nicheApplied} />
            )}
            {q &&
            data?.total === 0 &&
            (data?.nicheHints?.length ?? 0) === 0 &&
            (data?.orgHints?.length ?? 0) === 0 ? (
              <NoResultsAlert
                q={q}
                region={region}
                category={category}
                ikpu={ikpu}
                nicheSlug={niche}
                scope={apiScope}
                status={effectiveStatus}
                // What the link below KEEPS and the search still applies — sent
                // exactly as the search request above sends them, so «во всём
                // каталоге N» is the number the link opens.
                customer={customer}
                exclude={exclude}
                minBudget={minBudget ? Number(minBudget) : undefined}
                maxBudget={maxBudget ? Number(maxBudget) : undefined}
                awarded={awarded}
                // The same page minus the filters that hid the lots (category,
                // ИКПУ, niche, region, kind, marketplace) — the card offers it
                // with the unfiltered count when there is something to show.
                hrefWithoutNarrowing={hrefWithoutNarrowing(location.pathname, searchParams)}
              />
            ) : pastLastPage ? (
              <div
                data-testid="catalog-past-last-page"
                className="rounded-lg border border-edge bg-bone-surface px-5 py-8 text-center"
              >
                <p className="text-[15px] font-semibold text-ink">
                  {t('catalog.pastLastPage.title')}
                </p>
                <p className="mt-1 text-[13px] text-ink-soft">
                  {t('catalog.pastLastPage.desc', { pages: totalPages })}
                </p>
                <Link
                  to={firstPageOf(location.pathname, searchParams)}
                  className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-brand px-4 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid"
                >
                  {t('catalog.pastLastPage.first')}
                </Link>
              </div>
            ) : (
              <TenderList items={data?.items ?? []} compact={density === 'compact'} />
            )}
            {/* Spain: an empty result, for a reader with no saved search yet,
                offers the AI step back (QA 22.10, C11) — with a word or without. */}
            {AI_REENTRY && data?.total === 0 && (
              <div className="mt-3 flex justify-center">
                <AiOnboardingEntry />
              </div>
            )}
            {/* Past the last page the pager would clamp to «26 / 26» under the
                notice above — the notice's own link is the way back. */}
            {data && !pastLastPage && (
              <Pagination
                page={page}
                limit={PAGE_SIZE}
                total={data.total}
                shownTotal={pagerShownTotal}
                onPageChange={goToPage}
                hrefFor={pageHref}
              />
            )}
          </>
        )}
      </div>

      {/* #52 — public «Проекты по ведомствам» breakdown, /ifi only. */}
      {isIfi && <IfiProjectsPanel />}

      {/* The banner carries no margin of its own — see its note. */}
      <div className="mt-5">
        <ChannelSubscribeCta surface="catalog" />
        <UpgradeBanner />
      </div>
    </>
  );
}
